import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "LegalizacionViaticos";

// ======================================================
// NORMALIZAR TEXTO
// ======================================================
const normalizar = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

// ======================================================
// LEER PERMISOS DEL USUARIO
// ======================================================
const leerPermisos = () => {
  try {
    return JSON.parse(
      localStorage.getItem("permisos") ||
        localStorage.getItem("usuario") ||
        localStorage.getItem("user") ||
        localStorage.getItem("usuarios") ||
        "{}"
    );
  } catch {
    return {};
  }
};

// ======================================================
// BUSCAR MÓDULO DE PERMISOS
// ======================================================
const buscarModulo = (datos, nombreModulo) => {
  const modulos = Array.isArray(datos)
    ? datos
    : datos?.modulos ||
      datos?.permisos ||
      datos?.usuario?.modulos ||
      datos?.usuario?.permisos ||
      datos?.user?.modulos ||
      datos?.user?.permisos ||
      [];

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.moduloObjectId?.nombreModulo ||
        "";

      return normalizar(nombre) === normalizar(nombreModulo);
    }) || {}
  );
};

// ======================================================
// FORMATEAR FECHA DD/MM/AAAA
// ======================================================
const formatearFecha = (fecha) => {
  if (!fecha) return "-";

  const partes = String(fecha).split("T")[0].split("-");

  if (partes.length !== 3) return "-";

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

// ======================================================
// COMPONENTE
// ======================================================
const ListarLegalizacionViaticos = ({ refresh }) => {
  const navigate = useNavigate();
  const { id: agendaId } = useParams();

  const [legalizaciones, setLegalizaciones] = useState([]);
  const [loading, setLoading] = useState(false);

  const [eliminandoId, setEliminandoId] = useState("");

  const [descargandoId, setDescargandoId] = useState("");

  // ====================================================
  // PERMISOS
  // ====================================================
  const datosPermisos = leerPermisos();

  const rol = normalizar(
    datosPermisos?.rolObjectId?.nombreRol ||
      datosPermisos?.rol?.nombreRol ||
      datosPermisos?.rol?.nombre ||
      datosPermisos?.rol ||
      datosPermisos?.role ||
      datosPermisos?.usuario?.rol ||
      datosPermisos?.user?.rol ||
      ""
  );

  const esAdmin =
    rol.includes("superadmin") ||
    rol.includes("administrador");

  const modulo = buscarModulo(
    datosPermisos,
    MODULO
  );

  const puedeLeer =
    esAdmin ||
    modulo?.puedeLeer === true ||
    modulo?.leer === true ||
    modulo?.read === true ||
    modulo?.ver === true;

  const puedeCrear =
    esAdmin ||
    modulo?.puedeCrear === true ||
    modulo?.crear === true ||
    modulo?.create === true;

  const puedeActualizar =
    esAdmin ||
    modulo?.puedeActualizar === true ||
    modulo?.actualizar === true ||
    modulo?.editar === true ||
    modulo?.update === true;

  const puedeEliminar =
    esAdmin ||
    modulo?.puedeEliminar === true ||
    modulo?.eliminar === true ||
    modulo?.delete === true;

  // ====================================================
  // CONFIGURACIÓN AXIOS
  // ====================================================
  const config = {
    headers: {
      Authorization: `Bearer ${
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        ""
      }`,
    },
  };

  // ====================================================
  // VALIDAR SESIÓN
  // ====================================================
  const validarSesion = (error) => {
    if (error?.response?.status !== 401) {
      return false;
    }

    Swal.fire({
      icon: "warning",
      title: "Sesión expirada",
      text: "Por favor inicia sesión nuevamente.",
      confirmButtonText: "Aceptar",
    }).then(() => {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      navigate("/login");
    });

    return true;
  };

  // ====================================================
  // CARGAR LEGALIZACIÓN
  // ====================================================
  const cargarDatos = async () => {
    if (!puedeLeer || !agendaId) {
      setLegalizaciones([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/legalizacionesviaticos/agenda/${agendaId}`,
        config
      );

      if (Array.isArray(data?.legalizaciones)) {
        setLegalizaciones(
          data.legalizaciones
        );
      } else if (data?.legalizacion) {
        setLegalizaciones([
          data.legalizacion,
        ]);
      } else {
        setLegalizaciones([]);
      }
    } catch (error) {
      if (validarSesion(error)) {
        return;
      }

      if (
        error?.response?.status === 404
      ) {
        setLegalizaciones([]);
        return;
      }

      console.error(
        "Error cargando la legalización:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          "No se pudo cargar la legalización.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // EFECTO
  // ====================================================
  useEffect(() => {
    cargarDatos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaId, refresh]);

  // ====================================================
  // OBTENER ID DE UNA REFERENCIA
  // ====================================================
  const obtenerIdReferencia = (valor) => {
    if (!valor) return "";

    if (
      typeof valor === "object" &&
      valor !== null
    ) {
      return valor._id || "";
    }

    return String(valor);
  };

  // ====================================================
  // LIMPIAR PARTE DEL NOMBRE DEL ARCHIVO
  // ====================================================
  const limpiarParteNombreArchivo = (
    valor,
    fallback = ""
  ) => {
    const texto =
      String(valor ?? "").trim();

    if (!texto) {
      return fallback;
    }

    return (
      texto
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/\s+/g, " ")
        .trim() || fallback
    );
  };

  // ====================================================
  // OBTENER DOCUMENTO DEL INSTRUCTOR
  // ====================================================
  const obtenerDocumentoInstructor =
    async (legalizacion) => {
      // ==================================================
      // 1. INTENTAR DESDE LA LEGALIZACIÓN
      // ==================================================
      let documentoInstructor =
        legalizacion?.documentoInstructor ||
        legalizacion
          ?.agendaViaticoObjectId
          ?.contrato
          ?.instructor
          ?.documentoInstructor ||
        legalizacion
          ?.agendaViaticoObjectId
          ?.contrato
          ?.documentoInstructor ||
        "";

      if (documentoInstructor) {
        return String(
          documentoInstructor
        ).trim();
      }

      // ==================================================
      // 2. OBTENER LA AGENDA
      // ==================================================
      const agendaReferencia =
        legalizacion
          ?.agendaViaticoObjectId;

      const agendaReferenciaId =
        obtenerIdReferencia(
          agendaReferencia
        ) ||
        agendaId;

      let agendaCargada =
        typeof agendaReferencia ===
          "object" &&
        agendaReferencia !== null
          ? agendaReferencia
          : null;

      // Si todavía no tenemos el contrato/instructor
      // completo, consultamos la agenda directamente.
      if (
        agendaReferenciaId &&
        !agendaCargada
          ?.contrato
          ?.instructor
          ?.documentoInstructor
      ) {
        const responseAgenda =
          await axios.get(
            `/api/agendasviaticos/listid/${agendaReferenciaId}`,
            config
          );

        const dataAgenda =
          responseAgenda?.data || {};

        agendaCargada =
          dataAgenda?.agenda ||
          dataAgenda?.message ||
          agendaCargada;
      }

      // ==================================================
      // 3. INTENTAR DESDE LA AGENDA CONSULTADA
      // ==================================================
      documentoInstructor =
        agendaCargada
          ?.documentoInstructor ||
        agendaCargada
          ?.contrato
          ?.instructor
          ?.documentoInstructor ||
        agendaCargada
          ?.contrato
          ?.documentoInstructor ||
        "";

      if (documentoInstructor) {
        return String(
          documentoInstructor
        ).trim();
      }

      // ==================================================
      // 4. OBTENER EL CONTRATO
      // ==================================================
      const contratoReferencia =
        agendaCargada?.contrato ||
        agendaReferencia
          ?.contrato ||
        null;

      const contratoId =
        obtenerIdReferencia(
          contratoReferencia
        );

      if (contratoId) {
        const responseContrato =
          await axios.get(
            `/api/contratos/listid/${contratoId}`,
            config
          );

        const dataContrato =
          responseContrato?.data || {};

        const contratoCargado =
          dataContrato?.contrato ||
          dataContrato?.message ||
          null;

        documentoInstructor =
          contratoCargado
            ?.instructor
            ?.documentoInstructor ||
          contratoCargado
            ?.documentoInstructor ||
          "";
      }

      if (!documentoInstructor) {
        throw new Error(
          "No fue posible obtener el documento de identidad del instructor."
        );
      }

      return String(
        documentoInstructor
      ).trim();
    };

  // ====================================================
  // CONSTRUIR NOMBRE DE LA LEGALIZACIÓN
  //
  // GCCON-F-101 (ORDEN DE VIAJE) - DOCUMENTO INSTRUCTOR.docx
  // ====================================================
  const construirNombreLegalizacion =
    (
      numeroOrdenViaje,
      documentoInstructor
    ) => {
      const orden =
        limpiarParteNombreArchivo(
          numeroOrdenViaje
        );

      const documento =
        limpiarParteNombreArchivo(
          documentoInstructor
        );

      if (!orden) {
        throw new Error(
          "No fue posible obtener el número de orden de viaje."
        );
      }

      if (!documento) {
        throw new Error(
          "No fue posible obtener el documento de identidad del instructor."
        );
      }

      return `GCCON-F-101 (${orden}) - ${documento}.docx`;
    };

  // ====================================================
  // OBTENER MENSAJE ERROR BLOB
  // ====================================================
  const obtenerMensajeErrorBlob =
    async (
      error,
      mensajePorDefecto
    ) => {
      let mensaje =
        mensajePorDefecto;

      const responseData =
        error?.response?.data;

      if (
        responseData instanceof Blob
      ) {
        try {
          const texto =
            await responseData.text();

          if (texto) {
            try {
              const json =
                JSON.parse(texto);

              mensaje =
                json?.message ||
                mensaje;
            } catch {
              mensaje = texto;
            }
          }
        } catch {
          // Mantener mensaje por defecto
        }
      } else if (
        error?.response?.data?.message
      ) {
        mensaje =
          error.response.data.message;
      } else if (
        error?.message
      ) {
        mensaje =
          error.message;
      }

      return mensaje;
    };

  // ====================================================
  // DESCARGAR LEGALIZACIÓN
  // ====================================================
  const descargarLegalizacion =
    async (legalizacion) => {
      // ==================================================
      // VALIDAR ID
      // ==================================================
      if (!legalizacion?._id) {
        Swal.fire(
          "Error",
          "No se encontró el identificador de la legalización.",
          "error"
        );

        return;
      }

      try {
        // ==================================================
        // ACTIVAR ESTADO DESCARGANDO
        // ==================================================
        setDescargandoId(
          legalizacion._id
        );

        // ==================================================
        // CONSULTAR BACKEND
        // ==================================================
        const response =
          await axios.get(
            `/api/legalizacion/generar-docx/${legalizacion._id}`,
            {
              ...config,

              responseType:
                "blob",
            }
          );

        // ==================================================
        // CONTENT TYPE
        // ==================================================
        const contentType =
          response.headers?.[
            "content-type"
          ] ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

        // ==================================================
        // VALIDAR SI EL BACKEND DEVOLVIÓ JSON
        // ==================================================
        if (
          contentType.includes(
            "application/json"
          )
        ) {
          const textoError =
            await response.data.text();

          let mensaje =
            "No se pudo generar la legalización.";

          if (textoError) {
            try {
              const errorJson =
                JSON.parse(
                  textoError
                );

              mensaje =
                errorJson?.message ||
                mensaje;
            } catch {
              mensaje =
                textoError;
            }
          }

          throw new Error(
            mensaje
          );
        }

        // ==================================================
        // CREAR BLOB
        // ==================================================
        const blob =
          new Blob(
            [response.data],
            {
              type:
                contentType,
            }
          );

        // ==================================================
        // CREAR URL TEMPORAL
        // ==================================================
        const url =
          window.URL.createObjectURL(
            blob
          );

        // ==================================================
        // DOCUMENTO DEL INSTRUCTOR
        // ==================================================
        const documentoInstructor =
          await obtenerDocumentoInstructor(
            legalizacion
          );

        // ==================================================
        // ORDEN DE VIAJE
        // ==================================================
        const numeroOrdenViaje =
          legalizacion
            ?.numeroOrdenViaje ||
          "";

        // ==================================================
        // NOMBRE DEL ARCHIVO
        //
        // GCCON-F-101 (ORDEN DE VIAJE) - DOCUMENTO INSTRUCTOR.docx
        // ==================================================
        const nombreArchivo =
          construirNombreLegalizacion(
            numeroOrdenViaje,
            documentoInstructor
          );

        console.log(
          "[LEGALIZACIÓN] Orden:",
          numeroOrdenViaje
        );

        console.log(
          "[LEGALIZACIÓN] Documento instructor:",
          documentoInstructor
        );

        console.log(
          "[LEGALIZACIÓN] Nombre generado:",
          nombreArchivo
        );

        // ==================================================
        // CREAR ENLACE TEMPORAL
        // ==================================================
        const enlace =
          document.createElement(
            "a"
          );

        enlace.href =
          url;

        enlace.download =
          nombreArchivo;

        document.body.appendChild(
          enlace
        );

        // ==================================================
        // DESCARGAR
        // ==================================================
        enlace.click();

        // ==================================================
        // LIMPIAR
        // ==================================================
        enlace.remove();

        setTimeout(() => {
          window.URL.revokeObjectURL(
            url
          );
        }, 1000);

        // ==================================================
        // MENSAJE
        // ==================================================
        Swal.fire({
          icon:
            "success",

          title:
            "Legalización descargada",

          text:
            "El documento fue generado correctamente.",

          showConfirmButton:
            false,

          timer:
            1600,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error descargando la legalización:",
          error
        );

        const mensaje =
          await obtenerMensajeErrorBlob(
            error,
            "No se pudo descargar la legalización."
          );

        Swal.fire(
          "Error",
          mensaje,
          "error"
        );
      } finally {
        setDescargandoId("");
      }
    };

  // ====================================================
  // ELIMINAR LEGALIZACIÓN
  // ====================================================
  const eliminarLegalizacion =
    async (
      legalizacionId
    ) => {
      // ==================================================
      // VALIDAR PERMISOS
      // ==================================================
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar legalizaciones.",
          "warning"
        );

        return;
      }

      // ==================================================
      // CONFIRMACIÓN
      // ==================================================
      const confirmacion =
        await Swal.fire({
          title:
            "¿Eliminar legalización?",

          text:
            "También se eliminarán las fotografías y asistencias.",

          icon:
            "warning",

          showCancelButton:
            true,

          confirmButtonText:
            "Sí, eliminar",

          cancelButtonText:
            "Cancelar",

          confirmButtonColor:
            "#d33",
        });

      if (
        !confirmacion.isConfirmed
      ) {
        return;
      }

      try {
        // ==================================================
        // ESTADO
        // ==================================================
        setEliminandoId(
          legalizacionId
        );

        // ==================================================
        // ELIMINAR
        // ==================================================
        const { data } =
          await axios.delete(
            `/api/legalizacionesviaticos/${legalizacionId}`,
            config
          );

        // ==================================================
        // ACTUALIZAR LISTADO
        // ==================================================
        setLegalizaciones(
          (actuales) =>
            actuales.filter(
              (item) =>
                item._id !==
                legalizacionId
            )
        );

        // ==================================================
        // MENSAJE
        // ==================================================
        Swal.fire({
          icon:
            "success",

          title:
            data?.message ||
            "Legalización eliminada",

          showConfirmButton:
            false,

          timer:
            1500,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error eliminando la legalización:",
          error
        );

        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            "No se pudo eliminar la legalización.",
          "error"
        );
      } finally {
        setEliminandoId("");
      }
    };

  // ====================================================
  // DATOS DE LA TABLA
  // ====================================================
  const dataTabla =
    legalizaciones.map(
      (
        item,
        index
      ) => {
        // ==================================================
        // AGENDA
        // ==================================================
        const agenda =
          item.agendaViaticoObjectId ||
          {};

        // ==================================================
        // ESTADOS
        // ==================================================
        const eliminando =
          eliminandoId ===
          item._id;

        const descargando =
          descargandoId ===
          item._id;

        const procesando =
          eliminando ||
          descargando;

        // ==================================================
        // RETORNO FILA
        // ==================================================
        return {
          // ================================================
          // ITEM
          // ================================================
          item:
            index + 1,

          // ================================================
          // ORDEN
          // ================================================
          orden: (
            <span className="small fw-semibold">
              {item.numeroOrdenViaje ||
                "Sin número"}
            </span>
          ),

          // ================================================
          // AGENDA
          // ================================================
          agenda: (
            <div
              style={{
                minWidth:
                  180,
              }}
            >
              <div className="fw-semibold">
                {agenda.entidad ||
                  "Sin entidad"}
              </div>

              {agenda.ruta && (
                <small className="text-secondary">
                  {
                    agenda.ruta
                  }
                </small>
              )}
            </div>
          ),

          // ================================================
          // DESPLAZAMIENTO
          // ================================================
          desplazamiento: (
            <div
              className="small"
              style={{
                minWidth:
                  120,
              }}
            >
              <div>
                <span className="text-secondary">
                  Inicio:
                </span>{" "}

                <strong>
                  {formatearFecha(
                    agenda.fechaInicioDesplazamiento
                  )}
                </strong>
              </div>

              <div>
                <span className="text-secondary">
                  Fin:
                </span>{" "}

                <strong>
                  {formatearFecha(
                    agenda.fechaFinDesplazamiento
                  )}
                </strong>
              </div>
            </div>
          ),

          // ================================================
          // PRESENTACIÓN
          // ================================================
          presentacion: (
            <div
              style={{
                minWidth:
                  120,
              }}
            >
              <div className="fw-semibold">
                {item.ciudadPresentacion ||
                  "Sin ciudad"}
              </div>

              <small className="text-secondary">
                {formatearFecha(
                  item.fechaPresentacion
                )}
              </small>
            </div>
          ),

          // ================================================
          // ACCIONES
          // ================================================
          acciones: (
            <>
              {/* ============================================
                  ESCRITORIO
              ============================================ */}
              <div className="d-none d-md-flex gap-2 justify-content-end">

                {/* ==========================================
                    DESCARGAR
                ========================================== */}
                <button
                  type="button"
                  className="btn btn-success btn-sm d-flex align-items-center justify-content-center"
                  style={{
                    width:
                      34,

                    height:
                      34,
                  }}
                  title="Descargar legalización"
                  disabled={
                    loading ||
                    procesando
                  }
                  onClick={() =>
                    descargarLegalizacion(
                      item
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      descargando
                        ? "fa-spinner fa-spin"
                        : "fa-file-word"
                    }`}
                  ></i>
                </button>

                {/* ==========================================
                    EDITAR
                ========================================== */}
                {puedeActualizar && (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm d-flex align-items-center justify-content-center"
                    style={{
                      width:
                        34,

                      height:
                        34,
                    }}
                    title="Editar"
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      navigate(
                        `/instructor/legalizacionviaticos/update/${item._id}`
                      )
                    }
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {/* ==========================================
                    ELIMINAR
                ========================================== */}
                {puedeEliminar && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center justify-content-center"
                    style={{
                      width:
                        34,

                      height:
                        34,
                    }}
                    title="Eliminar"
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      eliminarLegalizacion(
                        item._id
                      )
                    }
                  >
                    <i
                      className={`fa-solid ${
                        eliminando
                          ? "fa-spinner fa-spin"
                          : "fa-trash"
                      }`}
                    ></i>
                  </button>
                )}
              </div>

              {/* ============================================
                  MÓVIL
              ============================================ */}
              <div className="d-flex d-md-none gap-2">

                {/* ==========================================
                    DESCARGAR
                ========================================== */}
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{
                    flex:
                      1,

                    minHeight:
                      55,
                  }}
                  disabled={
                    loading ||
                    procesando
                  }
                  onClick={() =>
                    descargarLegalizacion(
                      item
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      descargando
                        ? "fa-spinner fa-spin"
                        : "fa-file-word"
                    } mb-1`}
                  ></i>

                  <small>
                    {descargando
                      ? "Generando"
                      : "Descargar"}
                  </small>
                </button>

                {/* ==========================================
                    EDITAR
                ========================================== */}
                {puedeActualizar && (
                  <button
                    type="button"
                    className="btn btn-outline-warning btn-sm d-flex flex-column align-items-center justify-content-center"
                    style={{
                      flex:
                        1,

                      minHeight:
                        55,
                    }}
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      navigate(
                        `/instructor/legalizacionviaticos/update/${item._id}`
                      )
                    }
                  >
                    <i className="fa-solid fa-pen-to-square mb-1"></i>

                    <small>
                      Editar
                    </small>
                  </button>
                )}

                {/* ==========================================
                    ELIMINAR
                ========================================== */}
                {puedeEliminar && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm d-flex flex-column align-items-center justify-content-center"
                    style={{
                      flex:
                        1,

                      minHeight:
                        55,
                    }}
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      eliminarLegalizacion(
                        item._id
                      )
                    }
                  >
                    <i
                      className={`fa-solid ${
                        eliminando
                          ? "fa-spinner fa-spin"
                          : "fa-trash"
                      } mb-1`}
                    ></i>

                    <small>
                      {eliminando
                        ? "Eliminando"
                        : "Eliminar"}
                    </small>
                  </button>
                )}
              </div>
            </>
          ),
        };
      }
    );

  // ====================================================
  // COLUMNAS
  // ====================================================
  const columns = [
    {
      Header:
        "#",

      accessor:
        "item",
    },

    {
      Header:
        "Orden",

      accessor:
        "orden",
    },

    {
      Header:
        "Agenda",

      accessor:
        "agenda",
    },

    {
      Header:
        "Desplazamiento",

      accessor:
        "desplazamiento",
    },

    {
      Header:
        "Presentación",

      accessor:
        "presentacion",
    },

    {
      Header:
        "Acciones",

      accessor:
        "acciones",
    },
  ];

  // ====================================================
  // SIN PERMISO DE LECTURA
  // ====================================================
  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="alert alert-warning">
          <i className="fa-solid fa-lock me-2"></i>

          No tiene permisos para consultar las legalizaciones.
        </div>
      </div>
    );
  }

  // ====================================================
  // RENDER
  // ====================================================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">

        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* ==================================================
              ENCABEZADO
          ================================================== */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              {/* ==============================================
                  TÍTULO
              ============================================== */}
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Legalización de Viáticos
                </h5>

                <small className="text-secondary">
                  Gestión de legalización de agendas de viajes.
                </small>
              </div>

              {/* ==============================================
                  BOTONES SUPERIORES
              ============================================== */}
              <div className="d-flex gap-2 flex-nowrap">

                {/* VOLVER */}
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  disabled={
                    loading
                  }
                  onClick={() =>
                    navigate(
                      -1
                    )
                  }
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>

                {/* ACTUALIZAR */}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={
                    loading
                  }
                  onClick={
                    cargarDatos
                  }
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {/* ============================================
                    NUEVA LEGALIZACIÓN
                ============================================ */}
                {puedeCrear &&
                  agendaId && (
                    <button
                      type="button"
                      className="btn btn-dark"
                      disabled={
                        loading ||
                        legalizaciones.length >
                          0
                      }
                      onClick={() =>
                        navigate(
                          `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                        )
                      }
                      title={
                        legalizaciones.length >
                        0
                          ? "Esta agenda ya tiene una legalización"
                          : "Nueva legalización"
                      }
                    >
                      <i className="fa-solid fa-plus me-2"></i>

                      Nuevo
                    </button>
                  )}
              </div>
            </div>
          </div>

          {/* ==================================================
              CONTENIDO
          ================================================== */}
          <div>

            {/* ==================================================
                LOADING
            ================================================== */}
            {loading ? (
              <div className="text-center text-secondary py-5">

                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando legalización…
                </div>
              </div>
            ) : dataTabla.length >
              0 ? (

              /* ================================================
                 TABLA
              ================================================ */
              <div className="overflow-hidden">

                <DataTable
                  columns={
                    columns
                  }
                  data={
                    dataTabla
                  }
                />

              </div>
            ) : (

              /* ================================================
                 SIN REGISTROS
              ================================================ */
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  📄
                </div>

                <div className="fw-semibold">
                  No hay una legalización registrada
                </div>

                <div className="small mb-3">
                  Esta agenda todavía no tiene una legalización.
                </div>

                {/* ============================================
                    CREAR LEGALIZACIÓN
                ============================================ */}
                {puedeCrear &&
                  agendaId && (
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={() =>
                        navigate(
                          `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                        )
                      }
                    >
                      <i className="fa-solid fa-plus me-2"></i>

                      Crear legalización
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// EXPORTAR
// ======================================================
export default ListarLegalizacionViaticos;